import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import StructuredData from '@/components/feature/StructuredData';
import { articles } from '@/mocks/siteData';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '1', 10);

  const article = useMemo(
    () => articles.find((a) => a.id === articleId),
    [articleId],
  );

  const relatedArticles = useMemo(
    () => articles.filter((a) => a.category === article?.category && a.id !== articleId).slice(0, 3),
    [article, articleId],
  );

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-2">文章未找到</h2>
            <p className="text-foreground-500 mb-6">抱歉，您要查看的文章不存在或已被删除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-all duration-200 no-underline cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const contentHtml = article.content
    .split('\n')
    .map((line) => {
      const trimmed = line.trim();
      if (!trimmed) return '<br />';
      if (trimmed.startsWith('## ')) return `<h2 class="font-heading text-xl font-bold text-foreground-950 mt-8 mb-4">${trimmed.slice(3)}</h2>`;
      if (trimmed.startsWith('### ')) return `<h3 class="font-heading text-lg font-semibold text-foreground-900 mt-6 mb-3">${trimmed.slice(4)}</h3>`;
      if (trimmed.startsWith('| ')) return `<p class="text-sm text-foreground-600 my-1 font-mono">${trimmed}</p>`;
      if (/^\d+\.\s/.test(trimmed)) return `<p class="text-foreground-700 my-2"><strong>${trimmed}</strong></p>`;
      return `<p class="text-foreground-700 leading-relaxed mb-3">${trimmed}</p>`;
    })
    .join('');

  return (
    <>
      <StructuredData
        type="Article"
        articleData={{
          headline: article.title,
          description: article.excerpt,
          image: article.coverImage,
          datePublished: article.date,
          dateModified: article.date,
          authorName: article.author,
          url: `https://17.cn/article/${article.id}`,
        }}
      />
      <StructuredData
        type="BreadcrumbList"
        breadcrumbItems={[
          { name: '首页', url: 'https://17.cn/' },
          { name: article.categoryName, url: `https://17.cn/category/${article.category}` },
          { name: article.title, url: `https://17.cn/article/${article.id}` },
        ]}
      />

      <Navbar />
      <main className="pt-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.categoryName, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <div className="mb-8">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
              {article.categoryName}
            </span>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-400">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {article.readTime} 阅读
              </span>
            </div>

            <div className="flex flex-wrap gap-2 mt-3">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-0.5 rounded-full bg-background-200 text-foreground-500 text-xs"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-8">
            <img
              src={article.coverImage}
              alt={article.title}
              title={`${article.title} - 17.cn一起草每日大赛`}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="prose-custom text-base"
            dangerouslySetInnerHTML={{ __html: contentHtml }}
          />

          <div className="mt-12 pt-8 border-t border-background-200">
            <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-5">相关文章</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {relatedArticles.map((related) => (
                <Link
                  key={related.id}
                  to={`/article/${related.id}`}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="group bg-background-50 border border-background-200 rounded-xl overflow-hidden hover:border-primary-200 transition-all duration-300 no-underline cursor-pointer"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={related.coverImage}
                      alt={related.title}
                      title={`${related.title} - 17.cn一起草每日大赛`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h4 className="font-heading text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {related.title}
                    </h4>
                    <div className="mt-2 flex items-center gap-1 text-xs text-foreground-400">
                      <i className="ri-time-line"></i>
                      <span>{related.readTime} 阅读</span>
                    </div>
                  </div>
                </Link>
              ))}
              {relatedArticles.length === 0 && (
                <p className="text-sm text-foreground-400 col-span-full">暂无相关文章</p>
              )}
            </div>
          </div>
        </article>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}